import { useCallback, useEffect, useRef, useState } from 'react';

const WAIT_TIMEOUT = 10; // seconds

const useInactivityTimer = (start, onExpired) => {
  const [waitTimer, setWaitTimer] = useState(0);

  const startTimestamp = useRef(null);
  const animationFrame = useRef(null);
  const inactivityTimer = useRef(null);

  // ------------------------------------------------------------
  // ANIMATION TIMER (Progress Button)
  // ------------------------------------------------------------

  const animate = useCallback(() => {
    if (!startTimestamp.current) startTimestamp.current = Date.now();
    const elapsed = (Date.now() - startTimestamp.current) / 1000; // seconds
    setWaitTimer(elapsed);

    if (elapsed < WAIT_TIMEOUT) {
      animationFrame.current = requestAnimationFrame(animate);
    } else {
      setWaitTimer(WAIT_TIMEOUT);
      onExpired?.();
    }
  }, [onExpired]);

  const startWaitTimer = useCallback(() => {
    startTimestamp.current = Date.now();
    animationFrame.current = requestAnimationFrame(animate);
  }, [animate]);

  // ------------------------------------------------------------
  // RESET TIMERS Logic
  // ------------------------------------------------------------

  const resetTimers = useCallback(() => {
    if (start) {
      // Stop animation progress button
      startTimestamp.current = null;
      setWaitTimer(0);
      cancelAnimationFrame(animationFrame.current);

      // Reset time out from scratch
      clearTimeout(inactivityTimer.current);

      inactivityTimer.current = setTimeout(() => {
        startWaitTimer(); // Start the counter for the progress button
      }, 10000);
    }
  }, [start, startWaitTimer]);

  // ------------------------------------------------------------
  // CLEAR TIMERS
  // ------------------------------------------------------------

  const clearTimers = useCallback(() => {
    cancelAnimationFrame(animationFrame.current);
    clearTimeout(inactivityTimer.current);
  }, []);

  // ------------------------------------------------------------
  // EVENTS LISTENERS
  // ------------------------------------------------------------

  useEffect(() => {
    if (start) {
      resetTimers();

      window.addEventListener('mousemove', resetTimers);
      window.addEventListener('keydown', resetTimers);
      window.addEventListener('touchstart', resetTimers);
      window.addEventListener('touchmove', resetTimers);
      window.addEventListener('scroll', resetTimers);

      return () => {
        clearTimers();
        window.removeEventListener('mousemove', resetTimers);
        window.removeEventListener('keydown', resetTimers);
        window.removeEventListener('touchstart', resetTimers);
        window.removeEventListener('touchmove', resetTimers);
        window.removeEventListener('scroll', resetTimers);
      };
    }

    return () => {
      clearTimers();
    };
  }, []);

  // ------------------------------------------------------------
  // RETURN VALUES
  // ------------------------------------------------------------

  return {
    waitTimer: (waitTimer / WAIT_TIMEOUT) * 100,
  };
};

export default useInactivityTimer;
